import React, { useMemo, useRef, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ourJourney } from "../../utils/constants/Team";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import Fade from "react-reveal/Fade";
import LeftArrow from "../Icons/TeamAndCulture/OurJourney/LeftArrow";
import RightArrow from "../Icons/TeamAndCulture/OurJourney/RightArrow";
import CustomNextImage from "../common/CustomNextImage";

const OurJourney = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { isMobile } = useIsMobile();
  const swiperRef = useRef(null);
  const uniqueCount = ourJourney.length;
  // keep your repeated list for nice spacing on desktop
  const repeatCount = isMobile ? 1 : 3;
  const repeatedUsers = useMemo(
    () => Array(repeatCount).fill(ourJourney).flat(),
    [repeatCount]
  );
  const realActive = activeIndex % uniqueCount;
  const activeJourney = ourJourney[realActive];

  const handleSlideToLoop = ({ i }) => {
    const inst = swiperRef.current;
    if (!inst) return;
    if (typeof inst.slideToLoop === "function") {
      inst.slideToLoop(i);
    } else {
      inst.slideTo(i);
    }
  };

  return (
    <section className="relative z-[999]">
      <Fade bottom>
        <div className="flex items-center justify-center gap-4 mt-4">
          {/* Left Gradient Line + Star */}
          <div className="w-[85px] rounded-[2px] bg-[#473555] h-[2px] md:w-full md:max-w-[150px] md:h-[4px]" />

          {/* Heading */}
          <h6 className="text-[24px] font-lexend font-semibold text-center md:!w-fit lg:text-[40px]">
            Our Journey
          </h6>

          {/* Right Gradient Line + Star */}
          <div className="w-[85px] rounded-[2px] bg-[#473555] h-[2px] md:h-[4px] md:w-full md:max-w-[150px]" />
        </div>
      </Fade>

      {/* Date + arrows row */}
      <div className="mt-4 flex items-center justify-between mx-auto w-[90%] md:w-fit md:!justify-center md:!gap-20 md:!mt-6 lg:gap-40">
        <button onClick={() => swiperRef.current?.slidePrev()}>
          <LeftArrow className="w-[40px] cursor-pointer lg:w-[48px]" />
        </button>
        <div className="text-[32px] w-[230px] text-center font-semibold font-lexend text-[#958e9b] [-webkit-text-stroke:0.5px_rgba(208,203,209,0.5)] lg:text-[40px]">
          {activeJourney?.date}
        </div>

        <button onClick={() => swiperRef.current?.slideNext()}>
          <RightArrow className="w-[40px] cursor-pointer lg:w-[48px]" />
        </button>
      </div>

      <Swiper
        centeredSlides={true}
        loop={true}
        slidesPerView={"auto"}
        initialSlide={0}
        autoplay={{
          delay: 8000,
          disableOnInteraction: false,
        }}
        pagination={false}
        navigation={false}
        modules={[Autoplay, Pagination, Navigation]}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          setActiveIndex(swiper.realIndex);
        }}
        className="!mt-6 lg:!mt-10"
      >
        {repeatedUsers?.map((data, index) => {
          const isActive =
            index % ourJourney.length === activeIndex % ourJourney.length;
          return (
            <SwiperSlide
              key={index}
              className={`!w-[90%] relative z-[9998] transition-all duration-500 mx-4 md:!w-[480px] lg:!w-[580px] ${
                isActive ? "opacity-100 scale-100" : "opacity-50 scale-95"
              }`}
            >
              <div className="bg-white/5 backdrop-blur-3xl !border !border-[#666666] rounded-2xl text-white md:bg-[#221e24]">
                <div className="p-4 min-h-[72px] md:!p-5 md:w-[90%] md:min-h-[85px] lg:w-[85%] lg:!min-h-[104px]">
                  <p className="text-[16px] leading-[20px] font-lexend lg:text-[24px] lg:leading-[32px]">
                    {data?.description}
                  </p>
                </div>
                <CustomNextImage
                  src={data?.image}
                  alt={data?.title}
                  width={1000}
                  height={1000}
                  className="rounded-b-2xl object-cover w-full h-[202px] md:w-[480px] md:!h-[250px] lg:!w-[580px] lg:!h-[306px]"
                />
              </div>
            </SwiperSlide>
          );
        })}
        {/* Left fade */}
        <div className="hidden pointer-events-none absolute top-3 left-0 h-[95%] w-16 z-[9999] bg-gradient-to-r from-black/90 via-black/60 to-transparent md:block lg:w-60" />

        {/* Right fade */}
        <div className="hidden pointer-events-none absolute top-3 right-0 h-[95%] w-16  z-[9999] bg-gradient-to-l from-black/90 via-black/90/80 to-transparent md:block lg:w-60" />
      </Swiper>

      {/* Custom dots synced with the real slides (not the repeated ones) */}
      <div className="mt-4 flex justify-center gap-2">
        {Array.from({ length: uniqueCount }).map((_, i) => {
          const active = realActive === i;
          return (
            <button
              key={i}
              aria-label={`Go to ${i + 1}`}
              aria-current={active ? "true" : "false"}
              onClick={() => handleSlideToLoop({ i })}
              className={[
                "h-2 w-2 md:h-2.5 rounded-full transition md:w-2.5",
                active ? "bg-white" : "bg-white/30 hover:bg-white/60",
              ].join(" ")}
            />
          );
        })}
      </div>
    </section>
  );
};

export default OurJourney;
